import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClickAwayListener, Paper } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { pull, uniq } from 'lodash';
import { compose } from 'recompose';

import {
  BatchUpdateUserProfileStatusDocument,
  BatchUpdateUserProfileTypeDocument,
  QueryGetUserSearchesV2Args,
  SearchSortFieldsV2,
  UserProfile,
  UserStatus,
  UserType,
} from 'api';
import { Queries, Search } from 'components';
import { IWithApolloClient, withApolloClient } from 'providers';
import { batchUpdateUserStatus, batchUpdateUserType } from 'apollo/reducers';
import { useNotify } from 'hooks';

import { Columns, Header, Tabs, Total } from '../components/People';
import Loader from './../components/People/Loader';
import Table from './../components/People/Table';

interface IPropsEnhanced extends IWithApolloClient, WithStyles<typeof styles> {}

const LIMIT = 24;

const styles = (theme: Theme) =>
  createStyles({
    paper: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
    },
    column: {
      borderTop: `1px solid ${theme.custom.palette.grey3}`,
      borderBottom: `1px solid ${theme.custom.palette.grey3}`,
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(withApolloClient, withStyles(styles));

const People: FC<IPropsEnhanced> = ({ classes, apolloClient }) => {
  // Data State
  const [filter, setFilter] = useState(UserStatus.Active);
  const [checkedIds, setCheckedIds] = useState([]);

  // UI State
  const [term, setTerm] = useState('');
  const [total, setTotal] = useState(null);
  const [mutatedTotal, setMutatedTotal] = useState(null);
  const [focused, setFocused] = useState(false);
  const [allChecked, setAllChecked] = useState(false);

  const { t } = useTranslation();
  const notify = useNotify();

  const masterTotal = mutatedTotal || total;
  const variables: QueryGetUserSearchesV2Args = {
    user_status: filter,
    q: term,
    size: LIMIT,
    sort: SearchSortFieldsV2.FullName,
  };

  function getColumns(profiles: UserProfile[]) {
    return Columns({
      checkedIds,
      className: classes.column,
      isAllChecked: allChecked,
      onAllChecked: event => handleAllChecked(event, profiles),
      onChecked: handleChecked,
    });
  }

  async function handleUpdateStatus(userStatus: UserStatus) {
    const isSuspending = userStatus === UserStatus.Disabled;
    const statusMessage = isSuspending ? t('admin:suspended') : t('admin:activated');
    const updateStatusVariables = {
      ids: checkedIds,
      user_status: userStatus,
    };
    const notifyOptions = {
      i18n: {
        i18nKey: 'admin:userStatusSuccessMessage',
        count: checkedIds.length,
        values: { count: checkedIds.length, status: statusMessage },
      },
    };

    const mutation = await apolloClient
      .mutate({
        mutation: BatchUpdateUserProfileStatusDocument,
        variables: updateStatusVariables,
        update: (cache, result) => {
          batchUpdateUserStatus({ cache, result, variables });
        },
      })
      .catch(notify.mutationError);

    if (mutation) {
      notify.info(notifyOptions);
      setCheckedIds([]);
      setAllChecked(false);
      setMutatedTotal(masterTotal - checkedIds.length);
    }
  }

  async function handleUpdateType(userType: UserType) {
    const updateTypeVariables = {
      ids: checkedIds,
      user_type: userType,
    };
    const notifyOptions = {
      i18n: {
        i18nKey: 'admin:userTypeSuccessMessage',
        count: checkedIds.length,
        values: { count: checkedIds.length, userType: t(`common:${userType}`) },
      },
    };

    const mutation = await apolloClient
      .mutate({
        mutation: BatchUpdateUserProfileTypeDocument,
        variables: updateTypeVariables,
        update: (cache, result) => {
          batchUpdateUserType({ cache, result, variables });
        },
      })
      .catch(notify.mutationError);

    if (mutation) {
      notify.info(notifyOptions);
      setCheckedIds([]);
      setAllChecked(false);
    }
  }

  function handleReset(value: UserStatus) {
    setFilter(value);
    setCheckedIds([]);

    setAllChecked(false);
    setTotal(null);
    setMutatedTotal(null);
    setTerm('');
  }

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;

    setTerm(value);

    if (!!term === !value) {
      setCheckedIds([]);
      setAllChecked(false);
      setTotal(null);
      setMutatedTotal(null);
    }
  }

  function handleChecked(event: ChangeEvent<HTMLInputElement>) {
    const { checked, name } = event.target;

    checked ? checkedIds.unshift(name) : pull(checkedIds, name);
    setCheckedIds(uniq(checkedIds));
  }

  function handleAllChecked(event: ChangeEvent<HTMLInputElement>, profiles: UserProfile[]) {
    const { checked } = event.target;
    const ids = checked ? profiles.map(profile => profile.id) : [];

    setCheckedIds(ids);
    setAllChecked(checked);
  }

  function renderPeopleTable() {
    return (
      <Queries.GetUserSearchesV2 key="table" loader={<Loader rows={5} />} variables={variables}>
        {(profiles, count, onLoadMore) => {
          useEffect(() => {
            setTotal(count);
          });
          return (
            <Fragment>
              <Table
                emptyStateName="suspendedUsers"
                className={classes.column}
                columns={getColumns(profiles)}
                profiles={profiles}
                count={count}
                onLoadMore={onLoadMore}
              />
              {!!onLoadMore && <Loader rows={1} />}
            </Fragment>
          );
        }}
      </Queries.GetUserSearchesV2>
    );
  }

  return (
    <Paper>
      <Header
        filter={filter}
        count={checkedIds.length || masterTotal}
        onUpdateStatus={!!checkedIds.length && handleUpdateStatus}
        onUpdateType={!!checkedIds.length && handleUpdateType}
        tabs={<Tabs filter={filter} onChange={handleReset} />}
        total={<Total total={checkedIds.length || masterTotal} />}
        search={
          <ClickAwayListener onClickAway={() => setFocused(false)}>
            <Paper className={classes.paper}>
              <Search
                query={term}
                focused={focused}
                onChange={handleSearchChange}
                onClear={() => handleReset(UserStatus.Active)}
                onFocus={() => setFocused(true)}
              />
            </Paper>
          </ClickAwayListener>
        }
      />
      {renderPeopleTable()}
    </Paper>
  );
};

export default enhancers(People);
